/**
 * Copyright ©2022 Drivepoint
 */

import React from "react";
import {
  AccountBalance,
  AccountCircleOutlined,
  AccountTreeOutlined, AdminPanelSettingsOutlined, AirplaneTicketOutlined,
  AssessmentOutlined, AutoGraphOutlined, Dashboard, ElectricalServicesOutlined,
  Group, Groups2, Groups2Outlined, HubOutlined, Info, Inventory2Outlined, InventoryOutlined, LayersOutlined, LineAxisOutlined, Lock,
  ManageAccounts,
  ManageAccountsOutlined, MapOutlined, MonetizationOnOutlined, PeopleOutlined, Person,
  PersonOutline, ScienceOutlined,
  Settings, SpeedOutlined, StackedBarChartOutlined, Star, SummarizeOutlined, Warning
} from "@mui/icons-material";

const AnalyticsInfoPage = React.lazy(() => import("../pages/info/AnalyticsInfoPage/AnalyticsInfoPage"));
const BullBoardPage = React.lazy(() => import("../pages/administration/BullBoardPage"));
const BundlePage = React.lazy(() => import("../pages/BundlePage/BundlePage"));
const ChartSandboxPage = React.lazy(() => import("../pages/ChartSandboxPage/ChartSandboxPage"));
const CompanySettingsPage = React.lazy(() => import("../pages/CompanySettingsPage/CompanySettingsPage"));
const CsvImportPage = React.lazy(() => import("../pages/SourcesPage/CsvIngestion/CsvImportPage/CsvImportPage"));
const CreateCompanyPage = React.lazy(() => import("@pages/administration/CreateCompanyPage/CreateCompanyPage"));
const CreateGroupPage = React.lazy(() => import("../pages/NewTeamPage/GroupsPage/CreateGroupPage/CreateGroupPage"));
const CustomTokenPage = React.lazy(() => import("../pages/user/CustomTokenPage"));
const DashboardsInfoPage = React.lazy(() => import("../pages/info/DashboardsInfoPage/DashboardsInfoPage"));
const DataStudioPage = React.lazy(() => import("../pages/DataStudioPage"));
const DazzlerDashboardPage = React.lazy(() => import("@pages/dazzler/DazzlerDashboardPage/DazzlerDashboardPage"));
const DazzlerDashboardsPage = React.lazy(() => import("@pages/dazzler/DazzlerDashboardsPage/DazzlerDashboardsPage"));
const DazzlerStudioPage = React.lazy(() => import("@pages/dazzler/DazzlerStudioPage/DazzlerStudioPage"));
const DazzlerViewPage = React.lazy(() => import("@pages/dazzler/DazzlerViewPage/DazzlerViewPage"));
const EditGroupPage = React.lazy(() => import("../pages/NewTeamPage/GroupsPage/EditGroupPage/EditGroupPage"));
const EditUserPage = React.lazy(() => import("../pages/NewTeamPage/UsersPage/EditUserPage/EditUserPage"));
const ExperimentsPage = React.lazy(() => import("../pages/ExperimentsPage/ExperimentsPage"));
const ExternalLinkPage = React.lazy(() => import("../pages/ExternalLinkPage"));
const FinanceInfoPage = React.lazy(() => import("../pages/info/FinanceInfoPage"));
const FirebaseActionPage = React.lazy(() => import("../pages/user/FirebaseActionPage"));
const GenAIAssistantPage = React.lazy(() => import("../pages/genai/GenAIAssistantPage/GenAIAssistantPage"));
const GenAIAssistantsPage = React.lazy(() => import("../pages/genai/GenAIAssistantsPage/GenAIAssistantsPage"));
const GroupsPage = React.lazy(() => import("../pages/NewTeamPage/GroupsPage/GroupsPage"));
const MCPReportPage = React.lazy(() => import("../pages/MCPReportPage/MCPReportPage"));
const InfoPage = React.lazy(() => import("../pages/info/InfoPage"));
const IntuitDisconnectPage = React.lazy(() => import("../pages/intuit/IntuitDisconnectPage"));
const IntuitPage = React.lazy(() => import("../pages/intuit/IntuitPage"));
const InviteCompanyPage = React.lazy(() => import("../pages/administration/InviteCompanyPage"));
const InviteUserPage = React.lazy(() => import("../pages/user/InviteUserPage/InviteUserPage"));
const InviteUsersPage = React.lazy(() => import("../pages/administration/InviteUsersPage/InviteUsersPage"));
const LandingPage = React.lazy(() => import("../pages/LandingPage"));
const LegacyTeamPage = React.lazy(() => import("../pages/TeamPage/TeamPage"));
const ManageCompaniesPage = React.lazy(() => import("../pages/administration/ManageCompaniesPage"));
const McpServerPage = React.lazy(() => import("../pages/McpServerPage/McpServerPage"));
const ManageUsersPage = React.lazy(() => import("../pages/administration/ManageUsersPage"));
const CsvIngestionPage = React.lazy(() => import("../pages/SourcesPage/CsvIngestion/CsvIngestionPage/CsvIngestionPage"));
const NetsuiteSourceConfigurationPage = React.lazy(() => import("../pages/SourcesPage/Netsuite/NetsuiteSourceConfigurationPage"));
const FinancialsSourceConfigurationPage = React.lazy(() => import("@pages/SourcesPage/CustomFinancials/FinancialsSourceConfigurationPage"));
const NewTeamInviteUsersPage = React.lazy(() => import("../pages/NewTeamPage/UsersPage/InviteUsersPage/InviteUsersPage"));
const OmniDocumentPage = React.lazy(() => import("@pages/omni/OmniDocumentPage/OmniDocumentPage"));
const OmniPage = React.lazy(() => import("@pages/omni/OmniPage/OmniPage"));
const PermissionGroupsPage = React.lazy(() => import("../pages/PermissionGroupsPage/PermissionGroupsPage"));
const PlanSyncConfigurationPage = React.lazy(() => import("../pages/PlanSyncConfigurationPage/PlanSyncConfigurationPage"));
const PlansPage = React.lazy(() => import("@pages/plans/PlansPage/PlansPage"));
const PresencePage = React.lazy(() => import("../pages/administration/PresencePage/PresencePage"));
const PrintPage = React.lazy(() => import("@pages/PrintPage/PrintPage"));
const ProductsPage = React.lazy(() => import("../pages/ProductsPage/ProductsPage"));
const ProvisionCompanyPage = React.lazy(() => import("@pages/administration/ProvisionCompanyPage/ProvisionCompanyPage"));
const QuickstartWelcomePage = React.lazy(() => import("../pages/quickstart/QuickstartWelcomePage/QuickstartWelcomePage"));
const VendorCentralPreviewPage = React.lazy(() => import("../pages/dev/VendorCentralPreviewPage/VendorCentralPreviewPage"));
const ReportsPage = React.lazy(() => import("../pages/ReportsPage/ReportsPage"));
const SettingsPage = React.lazy(() => import("../pages/SettingsPage/SettingsPage"));
const SignInPage = React.lazy(() => import("../pages/user/SignInPage/SignInPage"));
const SignUpPage = React.lazy(() => import("../pages/user/SignUpPage"));
const SourcesPage = React.lazy(() => import("../pages/SourcesPage/SourcesPage"));
const TiktokCallbackPage = React.lazy(() => import("@pages/SourcesPage/Tiktok/TiktokCallbackPage/TiktokCallbackPage"));
const TiktokShopSelectorPage = React.lazy(() => import("@pages/SourcesPage/Tiktok/TiktokShopSelectorPage"));
const TiktokSourceConfigurationPage = React.lazy(() => import("@pages/SourcesPage/Tiktok/TiktokSourceConfigurationPage/TiktokSourceConfigurationPage"));
const UserDetailPage = React.lazy(() => import("../pages/TeamPage/UserDetailPage/UserDetailPage"));
const UserSettingsPage = React.lazy(() => import("../pages/UserSettingsPage/UserSettingsPage"));
const UsersPage = React.lazy(() => import("../pages/NewTeamPage/UsersPage/UsersPage"));
const VersionsDashboardPage = React.lazy(() => import("../pages/administration/VersionsDashboardPage/VersionsDashboardPage"));
const McpSettingsPage = React.lazy(() => import("../pages/administration/MCPSettingsPage/MCPSettingsPage"));
const MappingPage = React.lazy(() => import("../features/v2Mapping/pages/MappingPage"));
const MappingSettingsPage = React.lazy(() => import("../features/v2Mapping/pages/MappingSettingsPage"));

export default class ComponentRegistry {

  static classForName(name: string): any {
    switch (name) {
      case "AccountCircleOutlined": return AccountCircleOutlined;
      case "AccountTreeOutlined": return AccountTreeOutlined;
      case "AdminPanelSettingsOutlined": return AdminPanelSettingsOutlined;
      case "AirplaneTicketOutlined": return AirplaneTicketOutlined;
      case "AnalyticsInfoPage": return AnalyticsInfoPage;
      case "AssessmentOutlined": return AssessmentOutlined;
      case "AutoGraphOutlined": return AutoGraphOutlined;
      case "BullBoardPage": return BullBoardPage;
      case "BundlePage": return BundlePage;
      case "ChartSandboxPage": return ChartSandboxPage;
      case "CsvIngestionPage": return CsvIngestionPage;
      case "CompanySettingsPage": return CompanySettingsPage;
      case "CreateCompanyPage": return CreateCompanyPage;
      case "CsvImportPage": return CsvImportPage;
      case "CreateGroupPage": return CreateGroupPage;
      case "CustomTokenPage": return CustomTokenPage;
      case "Dashboard": return Dashboard;
      case "DashboardsInfoPage": return DashboardsInfoPage;
      case "DataStudioPage": return DataStudioPage;
      case "DazzlerDashboardPage": return DazzlerDashboardPage;
      case "DazzlerDashboardsPage": return DazzlerDashboardsPage;
      case "DazzlerStudioPage": return DazzlerStudioPage;
      case "DazzlerViewPage": return DazzlerViewPage;
      case "EditGroupPage": return EditGroupPage;
      case "EditUserPage": return EditUserPage;
      case "ElectricalServicesOutlined": return ElectricalServicesOutlined;
      case "ExperimentsPage": return ExperimentsPage;
      case "ExternalLinkPage": return ExternalLinkPage;
      case "FinanceInfoPage": return FinanceInfoPage;
      case "FirebaseActionPage": return FirebaseActionPage;
      case "GenAIAssistantPage": return GenAIAssistantPage;
      case "GenAIAssistantsPage": return GenAIAssistantsPage;
      case "Group": return Group;
      case "Groups2": return Groups2;
      case "Groups2Outlined": return Groups2Outlined;
      case "GroupsPage": return GroupsPage;
      case "MCPReportPage": return MCPReportPage;
      case "HubOutlined": return HubOutlined;
      case "Info": return Info;
      case "InfoPage": return InfoPage;
      case "IntuitDisconnectPage": return IntuitDisconnectPage;
      case "IntuitPage": return IntuitPage;
      case "Inventory2Outlined": return Inventory2Outlined;
      case "InventoryOutlined": return InventoryOutlined;
      case "InviteCompanyPage": return InviteCompanyPage;
      case "InviteUserPage": return InviteUserPage;
      case "InviteUsersPage": return InviteUsersPage;
      case "LandingPage": return LandingPage;
      case "LayersOutlined": return LayersOutlined;
      case "LegacyTeamPage": return LegacyTeamPage;
      case "LineAxisOutlined": return LineAxisOutlined;
      case "Lock": return Lock;
      case "ManageAccounts": return ManageAccounts;
      case "ManageAccountsOutlined": return ManageAccountsOutlined;
      case "ManageCompaniesPage": return ManageCompaniesPage;
      case "ManageUsersPage": return ManageUsersPage;
      case "McpServerPage": return McpServerPage;
      case "MonetizationOnOutlined": return MonetizationOnOutlined;
      case "NetsuiteSourceConfigurationPage": return NetsuiteSourceConfigurationPage;
      case "FinancialsSourceConfigurationPage": return FinancialsSourceConfigurationPage;
      case "NewTeamInviteUsersPage": return NewTeamInviteUsersPage;
      case "OmniDocumentPage": return OmniDocumentPage;
      case "OmniPage": return OmniPage;
      case "PeopleOutlined": return PeopleOutlined;
      case "PermissionGroupsPage": return PermissionGroupsPage;
      case "PersonOutline": return PersonOutline;
      case "Person": return Person;
      case "PlanSyncConfigurationPage": return PlanSyncConfigurationPage;
      case "PlansPage": return PlansPage;
      case "PresencePage": return PresencePage;
      case "PrintPage": return PrintPage;
      case "ProductsPage": return ProductsPage;
      case "ProvisionCompanyPage": return ProvisionCompanyPage;
      case "QuickstartWelcomePage": return QuickstartWelcomePage;
      case "VendorCentralPreviewPage": return VendorCentralPreviewPage;
      case "ReportsPage": return ReportsPage;
      case "ScienceOutlined": return ScienceOutlined;
      case "Settings": return Settings;
      case "SettingsPage": return SettingsPage;
      case "SignInPage": return SignInPage;
      case "SignUpPage": return SignUpPage;
      case "SourcesPage": return SourcesPage;
      case "SpeedOutlined": return SpeedOutlined;
      case "StackedBarChartOutlined": return StackedBarChartOutlined;
      case "Star": return Star;
      case "Summarize": return SummarizeOutlined;
      case "TeamPage": return UsersPage;
      case "TiktokCallbackPage": return TiktokCallbackPage;
      case "TiktokShopSelectorPage": return TiktokShopSelectorPage;
      case "TiktokSourceConfigurationPage": return TiktokSourceConfigurationPage;
      case "UserDetailPage": return UserDetailPage;
      case "UserSettingsPage": return UserSettingsPage;
      case "VersionsDashboardPage": return VersionsDashboardPage;
      case "Warning": return Warning;
      case "AccountBalance": return AccountBalance;
      case "MapOutlined": return MapOutlined;
      case "MappingPage": return MappingPage;
      case "MappingSettingsPage": return MappingSettingsPage;
      case "McpSettingsPage": return McpSettingsPage;
    }
  }

}
